<div class="add-loan-container" [ngClass]="{'wide': stepper?.selectedIndex === 2}">
  <h1>{{ isEdit ? 'Edit' : 'Add' }} Loan</h1>
  <div class="stepper-container" [ngClass]="{'wide-container': stepper.selectedIndex === 2}">
    <mat-stepper [linear]="isLinear" #stepper (selectionChange)="onStepChange($event)">
      <!-- Basic Details Step -->
      <mat-step [stepControl]="basicDetailsForm" class="basic-details-step">
        <form [formGroup]="basicDetailsForm" [ngClass]="{'wide-form': stepper.selectedIndex === 0}">
          <ng-template matStepLabel>Basic Details</ng-template>
          
          <!-- Loan Type Slider -->
          <div class="field-container loan-slider">
            <label class="loan-type-label">Loan Type</label>
            <mat-button-toggle-group formControlName="loanType" aria-label="Loan Type">
              <mat-button-toggle value="new">New Loan</mat-button-toggle>
              <mat-button-toggle value="mid-life">Mid-Life Loan</mat-button-toggle>
            </mat-button-toggle-group>
          </div>

          <!-- Contact Field -->
          <div class="field-container">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Contact</mat-label>
              <mat-select formControlName="contact" required [disabled]="loading()">
                @if(loading()) {
                  <mat-option disabled>
                    <mat-spinner diameter="20"></mat-spinner> Loading contacts...
                  </mat-option>
                }
                <mat-option *ngFor="let contact of (filteredContacts())" [value]="contact.id">
                  {{contact.first_name}} {{contact.last_name}} @if(contact.organization) { - {{contact.organization}} }
                </mat-option>
                @if(!loading() && (filteredContacts()).length === 0) {
                  <mat-option disabled>
                    No eligible contacts found. Please add contacts with type 'self' or 'borrower' first.
                  </mat-option>
                }
              </mat-select>
              @if(basicDetailsForm.get('contact')?.hasError('required')) {
                <mat-error>
                  Please select a contact before continuing.
                </mat-error>
              }
            </mat-form-field>
            <div class="field-hint">
              Select a contact from your contacts list as the primary contact. You can add more contacts later.
            </div>
          </div>

          <!-- Loan Name Field -->
          <div class="field-container">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Loan Name</mat-label>
              <input matInput
                     formControlName="loanName"
                     placeholder="Enter a Loan Name for your loan"
                     required>
              @if(basicDetailsForm.get('loanName')?.hasError('required')) {
                <mat-error>
                  Loan name is required
                </mat-error>
              }
            </mat-form-field>
            <div class="field-hint">
              The Loan Name for your loan can be any text string
            </div>
          </div>

          <!-- State Field -->
          <div class="field-container">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>State</mat-label>
              <mat-select formControlName="state" required>
                <mat-option value="active">Active</mat-option>
                <mat-option value="pending">Pending</mat-option>
                <mat-option value="paid-off">Paid Off</mat-option>
                <mat-option value="defaulted">Defaulted</mat-option>
              </mat-select>
              @if(basicDetailsForm.get('state')?.hasError('required')) {
                <mat-error>
                  Please select a state before continuing.
                </mat-error>
              }
            </mat-form-field>
          </div>

          <!-- Close Date Field -->
          <div class="field-container">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Close Date</mat-label>
              <input matInput
                     formControlName="closeDate"
                     [matDatepicker]="closeDatePicker"
                     placeholder="MM/DD/YYYY"
                     required>
              <mat-datepicker-toggle matIconSuffix [for]="closeDatePicker"></mat-datepicker-toggle>
              <mat-datepicker #closeDatePicker></mat-datepicker>
              @if(basicDetailsForm.get('closeDate')?.hasError('required')) {
                <mat-error>
                  Close date is required
                </mat-error>
              }
            </mat-form-field>
            <div class="field-hint">
              The Close Date is when the loan agreement was or will be signed and funded.
            </div>
          </div>

          <!-- Start Payment Date Field - Only show for Mid-Life Loans -->
          @if(basicDetailsForm.get('loanType')?.value === 'mid-life') {
            <div class="field-container">
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Start Payment Date</mat-label>
                <input matInput [matDatepicker]="startPaymentDatePicker" formControlName="startPaymentDate" required>
                <mat-datepicker-toggle matSuffix [for]="startPaymentDatePicker"></mat-datepicker-toggle>
                <mat-datepicker #startPaymentDatePicker></mat-datepicker>
              @if(basicDetailsForm.get('startPaymentDate')?.hasError('required')) {
                <mat-error>
                  Start payment date is required
                </mat-error>
              }
            </mat-form-field>
            <div class="field-hint">
              The Start Payment Date is when the first payment is/was due. Use this to enter loans mid life.
            </div>
          </div>
          }

          <!-- Next Payment Date Field -->
          <div class="field-container">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Next Payment Date</mat-label>
              <input matInput
                     formControlName="nextPaymentDate"
                     [matDatepicker]="nextPaymentDatePicker"
                     placeholder="MM/DD/YYYY"
                     required>
              <mat-datepicker-toggle matIconSuffix [for]="nextPaymentDatePicker"></mat-datepicker-toggle>
              <mat-datepicker #nextPaymentDatePicker></mat-datepicker>
              @if(basicDetailsForm.get('nextPaymentDate')?.hasError('required')) {
                <mat-error>
                  Next payment date is required
                </mat-error>
              }
            </mat-form-field>
            <div class="field-hint">
              The date when the next loan payment is due. 
            </div>
          </div>

          <!-- Late Fee  Field -->
          <div class="field-container">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Late Fee</mat-label>
              <input matInput
                     formControlName="lateFee"
                     placeholder="Late Fee"
                     required>
              @if(basicDetailsForm.get('lateFee')?.hasError('required')) {
                <mat-error>
                  Late fee is required
                </mat-error>
              }
            </mat-form-field>
            <div class="field-hint">
              The late fee charged for loans that are late on payments.
            </div>
          </div>

          <!-- Grace Period Field -->
          <div class="field-container">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Grace Period</mat-label>
              <input matInput
                     formControlName="gracePeriod"
                     placeholder="Grace Period"
                     required>
              @if(basicDetailsForm.get('gracePeriod')?.hasError('required')) {
                <mat-error>
                  Grace period is required
                </mat-error>
              }
            </mat-form-field>
            <div class="field-hint">
              The number of days after the loan is due that the loan is considered late.
            </div>
          </div>

          <!-- Fund from bank Field - Only show for New Loans and Active state -->
          @if(basicDetailsForm.get('loanType')?.value === 'new' && basicDetailsForm.get('state')?.value === 'active') {
            <div class="field-container">
              <mat-checkbox formControlName="fundFromBank" color="primary">
                Fund from bank
              </mat-checkbox>
              <div class="field-hint">
                If checked, a transaction will be created in the bank register when the loan is funded.
              </div>
            </div>
          }

          <!-- Step Actions -->
          <div class="step-actions">
            <button mat-button (click)="onCancel()">Cancel</button>
            <button mat-raised-button
                    color="primary"
                    matStepperNext
                    [disabled]="!basicDetailsForm.valid">
              Next
            </button>
          </div>
        </form>
      </mat-step>

      <!-- Payment Terms Step -->
      <mat-step [stepControl]="paymentTermsForm" class="payment-terms-step">
        <form [formGroup]="paymentTermsForm" [ngClass]="{'wide-form': stepper.selectedIndex === 1}">
          <ng-template matStepLabel>Payment Terms</ng-template>
          
          <!-- Payment Frequency Field -->
          <div class="field-container">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Payment Frequency</mat-label>
              <mat-select formControlName="frequency" required>
                <mat-option value="annually">Annually</mat-option>
                <mat-option value="semi-annually">Semi-Annually</mat-option>
                <mat-option value="quarterly">Quarterly</mat-option>
                <mat-option value="monthly">Monthly</mat-option>
                <mat-option value="bi-weekly">Bi-Weekly</mat-option>
                <mat-option value="weekly">Weekly</mat-option>
              </mat-select>
              @if(paymentTermsForm.get('frequency')?.hasError('required')) {
                <mat-error>
                  Please select a payment frequency
                </mat-error>
              }
              @if(paymentTermsForm.get('interestRate')?.hasError('min')) {
                <mat-error>
                  Interest rate must be greater than 0
                </mat-error>
              }
              @if(paymentTermsForm.get('interestRate')?.hasError('max')) {
                <mat-error>
                  Interest rate must be less than 100
                </mat-error>
              }
              @if(paymentTermsForm.get('interestRate')?.hasError('max')) {
                <mat-error>
                  Interest rate must be less than 100
                </mat-error>
              }
            </mat-form-field>
          </div>

          <!-- Principal Field -->
          <div class="field-container">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Principal</mat-label>
              <input matInput
                     formControlName="principal"
                     type="text"
                     placeholder="Enter loan principal amount"
                     required numberFormat>
              <span matTextSuffix>$</span>
              <button mat-icon-button
                      matSuffix
                      type="button"
                      [disabled]="!canCalculatePrincipal()"
                      (click)="calculatePrincipal()"
                      matTooltip="Calculate principal from payment, rate, and term">
                <mat-icon>calculate</mat-icon>
              </button>
              @if(paymentTermsForm.get('principal')?.hasError('required')) {
                <mat-error>
                  Principal is required
                </mat-error>
              }
              @if(paymentTermsForm.get('principal')?.hasError('min')) {
                <mat-error>
                  Principal must be greater than 0
                </mat-error>
              }
            </mat-form-field>
          </div>

          <!-- Monthly Payment Field -->
          <div class="field-container">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Monthly Payment</mat-label>
              <input matInput
                     formControlName="monthlyPayment"
                     type="text"
                     placeholder="Enter monthly payment amount" numberFormat>
              <span matTextSuffix>$</span>
              <button mat-icon-button
                      matSuffix
                      type="button"
                      [disabled]="!canCalculatePayment()"
                      (click)="calculatePayment()"
                      matTooltip="Calculate payment from principal, rate, and term">
                <mat-icon>calculate</mat-icon>
              </button>
              @if(paymentTermsForm.get('monthlyPayment')?.hasError('min')) {
                <mat-error>
                  Payment must be greater than 0
                </mat-error>
              }
            </mat-form-field>
          </div>

          <!-- Interest Rate Field -->
          <div class="field-container">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Interest Rate</mat-label>
              <input matInput
                     formControlName="interestRate"
                     type="number"
                     step="0.01"
                     min="0"
                     max="100"
                     placeholder="Enter annual interest rate">
              <span matTextSuffix>%</span>
              <button mat-icon-button
                      matSuffix
                      type="button"
                      [disabled]="!canCalculateInterest()"
                      (click)="calculateInterest()"
                      matTooltip="Calculate rate from principal, payment, and term">
                <mat-icon>calculate</mat-icon>
              </button>
            </mat-form-field>
          </div>

          <!-- Term Field -->
          <div class="field-container">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Term ({{getTermLabel()}})</mat-label>
              <input matInput
                     formControlName="term"
                     type="number"
                     step="1"
                     min="1"
                     [max]="getMaxTermForFrequency()"
                     [placeholder]="'Enter loan term in ' + getTermLabel().toLowerCase()">
              <span matTextSuffix>{{getTermLabel().toLowerCase()}}</span>
              <button mat-icon-button
                      matSuffix
                      type="button"
                      [disabled]="!canCalculateTerm()"
                      (click)="calculateTerm()"
                      matTooltip="Calculate term from principal, payment, and rate">
                <mat-icon>calculate</mat-icon>
              </button>
              @if(paymentTermsForm.get('term')?.hasError('min')) {
                <mat-error>
                  Term must be at least 1 {{getTermLabel().toLowerCase()}}
                </mat-error>
              }
              @if(paymentTermsForm.get('termMonths')?.hasError('max')) {
                <mat-error>
                  Term must be less than 600 months (50 years)
                </mat-error>
              }
            </mat-form-field>
          </div>

          <!-- Step Actions -->
          <div class="step-actions">
            <button mat-button matStepperPrevious>Back</button>
            <button mat-raised-button
                    color="primary"
                    matStepperNext
                    [disabled]="!paymentTermsForm.valid">
              Next
            </button>
          </div>
        </form>
      </mat-step>

      <mat-step class="review-step">
        <ng-template matStepLabel>Review & Create</ng-template>
        
        <!-- Loan Summary -->
        <div class="review-section">
          <h3>Loan Summary</h3>
          <mat-card>
            <mat-card-content>
              <div class="summary-grid">
                <!-- Basic Details -->
                <div class="summary-section">
                  <h4>Basic Details</h4>
                  <p><strong>Contact:</strong> {{  get_name(basicDetailsForm.get('contact')?.value) }} </p>
                  <p><strong>Loan Name:</strong> {{basicDetailsForm.get('loanName')?.value}}</p>
                  <p><strong>Close Date:</strong> {{basicDetailsForm.get('closeDate')?.value | date}}</p>
                  <p><strong>Start Payment Date:</strong> {{basicDetailsForm.get('startPaymentDate')?.value | date}}</p>
                  <p><strong>Next Payment Date:</strong> {{basicDetailsForm.get('nextPaymentDate')?.value | date}}</p>
                  <p><strong>Fund from bank:</strong> {{basicDetailsForm.get('fundFromBank')?.value ? 'Yes' : 'No'}}</p>
                </div>

                <!-- Payment Terms -->
                <div class="summary-section">
                  <h4>Payment Terms</h4>
                  <p><strong>Principal:</strong> ${{LoanUtils.parseFormattedNumber(paymentTermsForm.get('principal')?.value) | number:'1.2-2'}}</p>
                  <p><strong>Payment:</strong> ${{LoanUtils.parseFormattedNumber(paymentTermsForm.get('monthlyPayment')?.value) | number:'1.2-2'}}</p>
                  <p><strong>Interest Rate:</strong> {{paymentTermsForm.get('interestRate')?.value}}%</p>
                  <p><strong>Term:</strong> {{paymentTermsForm.get('term')?.value}} {{paymentTermsForm.get('frequency')?.value}}</p>
                </div>

                <!-- Calculated Totals -->
                <div class="summary-section">
                  <div class="header-with-button">
                    <h4>Loan Analysis</h4>
                  </div>
                  <p><strong>Total of Payments:</strong> {{totalPayments | currency}}</p>
                  <p><strong>Total Interest:</strong> {{totalInterest | currency}}</p>
                  <p><strong>Volume Rate of Return:</strong> {{vrr | number:'1.2-2'}}%</p>
                </div>
              </div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Amortization Schedule -->
        <div class="review-section amortization-section">
          <div class="header-with-button">
            <h3>Amortization Schedule</h3>
            @if(basicDetailsForm.get('loanType')?.value === 'mid-life') {
              <button mat-raised-button color="primary" type="button" (click)="recalculateAmortizationFromMidLife()">
                <mat-icon>refresh</mat-icon> Recalculate Amortization
              </button>
            }
          </div>
          <mat-card>
            <mat-card-content>
              <table mat-table [dataSource]="getAmortizationScheduleFromNextPayment()" class="mat-elevation-z8">
                <!-- Payment Number Column -->
                <ng-container matColumnDef="paymentNumber">
                  <th mat-header-cell *matHeaderCellDef>No.</th>
                  <td mat-cell *matCellDef="let row">{{row.paymentNumber}}</td>
                </ng-container>

                <!-- Payment Date Column -->
                <ng-container matColumnDef="paymentDate">
                  <th mat-header-cell *matHeaderCellDef>Date</th>
                  <td mat-cell *matCellDef="let row">{{row.paymentDate | date}}</td>
                </ng-container>

                <!-- Status Column -->
                <ng-container matColumnDef="status">
                  <th mat-header-cell *matHeaderCellDef>Status</th>
                  <td mat-cell *matCellDef="let row" [ngClass]="{'paid-payment': row.paid}">
                    @if(row.paymentNumber === 0) {
                      Funding
                    }
                    @if(row.paymentNumber !== 0) {
                      {{row.paid ? 'Paid' : 'Pending'}}
                    }
                  </td>
                </ng-container>

                <!-- Payment Column -->
                <ng-container matColumnDef="payment">
                  <th mat-header-cell *matHeaderCellDef>Payment</th>
                  <td mat-cell *matCellDef="let row">{{row.payment | currency}}</td>
                </ng-container>

                <!-- Principal Column -->
                <ng-container matColumnDef="principal">
                  <th mat-header-cell *matHeaderCellDef>Principal</th>
                  <td mat-cell *matCellDef="let row">{{row.principal | currency}}</td>
                </ng-container>

                <!-- Interest Column -->
                <ng-container matColumnDef="interest">
                  <th mat-header-cell *matHeaderCellDef>Interest</th>
                  <td mat-cell *matCellDef="let row">{{row.interest | currency}}</td>
                </ng-container>

                <!-- Balance Column -->
                <ng-container matColumnDef="remainingBalance">
                  <th mat-header-cell *matHeaderCellDef>Balance</th>
                  <td mat-cell *matCellDef="let row; let i = index">
                    @if(i === 0 && basicDetailsForm.get('loanType')?.value === 'mid-life') {
                      <mat-form-field appearance="outline" class="balance-edit">
                        <input matInput
                               type="text"
                               [formControl]="paymentTermsForm.get('midLifeBalance')"
                               placeholder="{{row.remainingBalance}}" numberFormat>
                        <span matPrefix>$</span>
                      </mat-form-field>
                    } @else {
                      <span [style.color]="row.remainingBalance < 0 ? '#f44336' : 'inherit'" 
                            [style.font-weight]="row.remainingBalance < 0 ? 'bold' : 'normal'">
                        {{row.remainingBalance | currency}}
                      </span>
                    }
                  </td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="['paymentNumber', 'paymentDate', 'status', 'payment', 'principal', 'interest', 'remainingBalance']"></tr>
                <tr mat-row *matRowDef="let row; columns: ['paymentNumber', 'paymentDate', 'status', 'payment', 'principal', 'interest', 'remainingBalance'];"></tr>
              </table>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Step Actions -->
        <div class="step-actions">
          <button mat-button matStepperPrevious>Back</button>
          <button mat-button (click)="onCancel()">Cancel</button>
          @if(isEdit) {
            <button mat-raised-button class="del-button" type="button" (click)="delete()">
              Delete Loan
            </button>
          }
          <button mat-raised-button
                  color="primary"
                  (click)="onSubmit()"
                  [disabled]="!basicDetailsForm.valid || !paymentTermsForm.valid">
            {{ isEdit ? 'Update' : 'Create' }} Loan
          </button>
        </div>
      </mat-step>
    </mat-stepper>
  </div>

  </div>
